/**
 * People (aka Users) admin tab.
 *
 * Extracted from AdminApp.jsx (Feb 2026 modularisation phase 2). Renders
 * the full participant/manager/channel-partner CRUD list, invites,
 * demo-prefill controls, and the Sales Cert report drawer entry point.
 *
 * The drawer + RetestConfirmModal live in `admin/ResultsTab.jsx`.
 */
import { useEffect, useState, useRef } from "react";
import { Plus, X, Users, Download, Upload, Wand2, RotateCcw, Mail, Send, Sparkles, FileText, ShieldCheck, Eye, Pencil, Trash2, ChevronDown, ChevronUp, CheckCircle2, AlertTriangle, Save, Loader2 } from "lucide-react";
import api, { formatApiError, API } from "../../api";
import { useAuth } from "../../contexts/AuthContext";
import { trackDownload, trackClick } from "../../hooks/useActivityLogger";
import { PageHeader, Loading, Err, KpiTile } from "./_shared";
import { RetestConfirmModal, SalesCertReportDrawer } from "./ResultsTab";

export default function UsersTab({ isClient = false }) {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("participant");
  const [showUpload, setShowUpload] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [selCycle, setSelCycle] = useState("");
  const [uploading, setUploading] = useState(false);
  const [drawerUser, setDrawerUser] = useState(null);  // selected participant for the deep-dive drawer
  const [retesting, setRetesting] = useState(null);    // {user, phase: 'confirm'|'running'|'done', result?}
  const fileRef = useRef(null);

  const load = () => api.get(`/admin/users${filter ? `?role=${filter}` : ""}`).then((r) => setUsers(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => { api.get("/admin/cycles").then((r) => { setCycles(r.data); if (r.data[0]) setSelCycle(r.data[0].id); }); }, []);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setUploading(true); setUploadResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      if (selCycle) form.append("cycle_id", selCycle);
      const r = await fetch(`${API}/admin/users/bulk`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("vi_token")}` },
        body: form,
      });
      const data = await r.json();
      setUploadResult(data);
      load();
    } catch (e) {
      setUploadResult({ ok: false, error: e.message });
    } finally {
      setUploading(false);
    }
  };

  const csvTemplate = "name,email,role,role_level,region,manager_email,channel_partner_email,employee_id\nJohn Doe,john.doe@vi.com,participant,L1,North,manager@vi.com,partner@channel.com,VI0001\n";

  return (
    <div className="space-y-8 fade-in">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageHeader eyebrow="USERS & ROSTER" title="People" subtitle={isClient ? "Your enrolled sales team across roles and regions." : "Manage participants, managers, and external channel-partner raters."} />
        {!isClient && (
          <div className="flex gap-2">
            <a href={`data:text/csv;charset=utf-8,${encodeURIComponent(csvTemplate)}`} download="roster_template.csv" className="btn btn-ghost !text-xs" data-testid="download-csv-template">
              <Download size={14} /> Template
            </a>
            <button onClick={() => setShowUpload(!showUpload)} className="btn btn-primary !text-xs" data-testid="bulk-upload-toggle">
              <Upload size={14} /> Bulk upload
            </button>
          </div>
        )}
      </div>

      {showUpload && !isClient && (
        <div className="card p-6 space-y-4 fade-in" data-testid="bulk-upload-panel">
          <h3 className="font-heading font-bold text-lg">Bulk upload roster</h3>
          <p className="text-[13px] text-[#525252]">Upload a CSV with columns: <code className="text-[12px] bg-[#F3F4F6] px-1.5 py-0.5">name, email, role, role_level, region, manager_email, channel_partner_email, employee_id</code>. Existing emails are skipped. Default password for new users: <code>Welcome@123</code>.</p>
          <div className="flex gap-3 items-end flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#525252] mb-1">Assign new participants to cycle</label>
              <select value={selCycle} onChange={(e) => setSelCycle(e.target.value)} className="input">
                <option value="">— None —</option>
                {cycles.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#525252] mb-1">CSV file</label>
              <input ref={fileRef} type="file" accept=".csv" className="input" data-testid="csv-file-input" />
            </div>
            <button onClick={upload} disabled={uploading} className="btn btn-primary" data-testid="csv-upload-submit">
              {uploading ? "Uploading…" : <><Upload size={14} /> Upload</>}
            </button>
          </div>
          {uploadResult && (
            <div className={`mt-3 p-4 border-l-4 ${uploadResult.ok ? "bg-[#E6F4EC] border-[#008A3D]" : "bg-[#FEE2E2] border-[#EE2737]"}`}>
              {uploadResult.ok ? (
                <div>
                  <div className="font-heading font-bold text-sm mb-1 text-[#008A3D]">Upload complete</div>
                  <div className="text-[13px]">Created: <strong>{uploadResult.created}</strong> · Skipped: <strong>{uploadResult.skipped}</strong> · Errors: <strong>{uploadResult.errors}</strong> · Assignments created: <strong>{uploadResult.assignments_created}</strong></div>
                </div>
              ) : (
                <div className="text-[#991B1B] text-sm">Error: {uploadResult.error || JSON.stringify(uploadResult)}</div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {["participant", "manager", "channel_partner", "admin"].map((r) => (
          <button key={r}
            onClick={() => setFilter(r)}
            data-testid={`filter-${r}`}
            className={`btn ${filter === r ? "btn-primary" : "btn-secondary"} !py-2 !px-4 !text-xs uppercase tracking-wider`}
          >{r.replace("_", " ")}</button>
        ))}
      </div>
      <div className="card overflow-hidden">
        <table className="data-table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Level</th><th>Program</th><th>Region</th><th>Emp ID</th><th></th></tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isParticipant = u.role === "participant";
              const setLevel = async (e, next) => {
                e.stopPropagation();
                // Optimistic update — participants using the People page
                // usually expect their choice to reflect immediately. If the
                // PATCH fails we reload from the server to reset.
                setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, program_level: next } : x));
                try {
                  await api.patch(`/admin/users/${u.id}`, { program_level: next });
                } catch { load(); }
              };
              return (
                <tr
                  key={u.id}
                  onClick={isParticipant ? () => setDrawerUser(u) : undefined}
                  className={isParticipant ? "cursor-pointer hover:bg-[#F5F3EE] transition-colors" : ""}
                  data-testid={isParticipant ? `people-row-${u.email}` : undefined}
                >
                  <td className="font-semibold">
                    {isParticipant ? (
                      <span className="text-[#0A0A0A] hover:text-[#EE2737]">{u.name}</span>
                    ) : u.name}
                  </td>
                  <td className="text-[#525252]">{u.email}</td>
                  <td>{u.role_level ? <span className="badge badge-red">{u.role_level}</span> : <span className="text-[#878787]">—</span>}</td>
                  <td>
                    {isParticipant ? (
                      <select
                        value={u.program_level || ""}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setLevel(e, e.target.value)}
                        className="input !py-1 !px-2 !text-xs !w-[110px]"
                        data-testid={`program-level-${u.email}`}
                      >
                        <option value="Ascend">Ascend</option>
                        <option value="Summit">Summit</option>
                        <option value="">— unset —</option>
                      </select>
                    ) : (
                      <span className="text-[#878787]">—</span>
                    )}
                  </td>
                  <td>{u.region || "—"}</td>
                  <td className="text-[#525252]">{u.employee_id || "—"}</td>
                  <td className="text-right pr-4">
                    {isParticipant && (
                      <div className="flex items-center justify-end gap-3">
                        <button
                          onClick={(e) => { e.stopPropagation(); setRetesting({ user: u, phase: "confirm" }); }}
                          className="text-[10px] uppercase tracking-widest text-[#DC2626] font-bold hover:underline"
                          data-testid={`retest-btn-${u.email}`}
                          title="Wipe this candidate's Sales Cert data so they can restart the flow"
                        >
                          ↻ Retest
                        </button>
                        <span className="text-[10px] uppercase tracking-widest text-[#EE2737] font-bold">View report →</span>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {drawerUser && (
        <SalesCertReportDrawer user={drawerUser} onClose={() => setDrawerUser(null)} />
      )}
      {retesting && (
        <RetestConfirmModal
          state={retesting}
          onCancel={() => setRetesting(null)}
          onConfirm={async () => {
            setRetesting((s) => ({ ...s, phase: "running" }));
            try {
              const r = await api.post(`/admin/salescert/reset-user/${retesting.user.id}`);
              setRetesting((s) => ({ ...s, phase: "done", result: r.data }));
            } catch (e) {
              setRetesting((s) => ({ ...s, phase: "done", result: { error: formatApiError(e) } }));
            }
          }}
        />
      )}
    </div>
  );
}

// RetestConfirmModal, SalesCertReportDrawer, Results, ResultDetail,
// TranscriptViewer moved to admin/ResultsTab.jsx.


